// Implements: F-UIX-009, F-UIX-007, F-UIX-019
/**
 * The sidebar of the redesign mockup: brand, organization switcher, the navigation groups and a foot
 * (usage meter, theme, user menu). It draws what `navigation.ts` decided; it decides nothing.
 *
 * In the rail every label is visually hidden but still the link's accessible name, so the collapsed
 * sidebar reads exactly as the expanded one to a screen reader.
 */

import { Fragment } from "react";
import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../shared/i18n";
import { Icon } from "../../shared/ui/atoms/Icon";
import type { NavGroup } from "./navigation";
import styles from "./Sidebar.module.css";

export interface SidebarContentProps {
  readonly groups: readonly NavGroup[];
  readonly activeId: string | null;
  readonly rail: boolean;
  /** Touch-sized (44px) rows, for the drawer below 1024px. */
  readonly touch?: boolean | undefined;
  /** The organization switcher, above the navigation. */
  readonly switcher: ReactNode;
  /** Meter, theme and user menu, below the navigation. */
  readonly foot: ReactNode;
  /** A number drawn at the end of an item (by item id), with its sentence for assistive tools. */
  readonly counts?: Readonly<Record<string, { readonly n: number; readonly description: string }>> | undefined;
  /** Called after a link is followed (the mobile drawer closes on it). */
  readonly onNavigate?: (() => void) | undefined;
}

export function SidebarContent({
  groups,
  activeId,
  rail,
  touch = false,
  switcher,
  foot,
  counts,
  onNavigate,
}: SidebarContentProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={[styles.sidebar, rail ? styles.rail : undefined, touch ? styles.touch : undefined].filter(Boolean).join(" ")}>
      <div className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.logo} aria-hidden="true">
            <Icon name="mark" size="sm" />
          </span>
          <span className={styles.label}>{t("app.shell.brand")}</span>
        </div>
        {switcher}
      </div>
      <nav className={styles.nav} aria-label={t("app.shell.sections")}>
        {groups.map((group) => (
          <div
            key={group.id}
            className={[styles.group, group.id === "platform" ? styles.platform : undefined].filter(Boolean).join(" ")}
            role="group"
            aria-label={t(group.labelKey)}
          >
            <div className={styles.groupLabel} aria-hidden="true">
              <span className={styles.label}>{t(group.labelKey)}</span>
              {group.id === "platform" ? (
                <span className={styles.badge}>{t("app.nav.platformBadge")}</span>
              ) : null}
            </div>
            {group.items.map((item) => {
              const count = counts?.[item.id];
              const describedBy = `nav-count-${item.id}`;
              return (
                <Fragment key={item.id}>
                  <Link
                    to={item.href}
                    className={styles.item}
                    aria-current={item.id === activeId ? "page" : undefined}
                    aria-describedby={count === undefined ? undefined : describedBy}
                    onClick={onNavigate}
                  >
                    <Icon name={item.icon} />
                    <span className={styles.label}>{t(item.labelKey)}</span>
                    {count === undefined ? null : (
                      <span className={styles.count} aria-hidden="true">
                        {count.n}
                      </span>
                    )}
                  </Link>
                  {count === undefined ? null : (
                    <span id={describedBy} className={styles.srOnly}>
                      {count.description}
                    </span>
                  )}
                </Fragment>
              );
            })}
          </div>
        ))}
      </nav>
      <div className={styles.foot}>{foot}</div>
    </div>
  );
}
